import type { Dictionary } from "@kensio/pinyinjs";

import type { CopyFlavours } from "./clipboard.js";
import type { ServedTier } from "./dictionary.js";
import { wordsCopy } from "./segmenter-copy.js";
import type { SegmenterElements } from "./segmenter-elements.js";
import { SegmenterView } from "./segmenter-view.js";
import {
  orderedWords,
  type SegmentedWord,
  segmentedWords,
  type WordOrder,
} from "./segmented-words.js";
import { currentState, startingState, type Startup } from "./tool-state.js";
import { rememberUrl, type WritableState } from "./url-state.js";

/**
 * What the segmenter knows: a dictionary, a text and an order.
 *
 * Apart from `segmenter-tool.ts`, which is the page half — the buttons, the
 * listeners and the one exported function the bundle calls. Everything here
 * can be asked for an answer without a click having happened, and nothing here
 * knows which control asked.
 */
export class SegmenterSession {
  readonly #elements: SegmenterElements;
  readonly #view: SegmenterView;
  readonly #startup: Startup;

  #dictionary: Dictionary | undefined;
  #words: readonly SegmentedWord[] = [];
  #order: WordOrder = "count";

  constructor(elements: SegmenterElements, dictionaryUrl: string) {
    this.#elements = elements;
    this.#view = new SegmenterView(elements);
    this.#startup = startingState(elements, dictionaryUrl, () => {
      this.#draw();
    });

    // A link can carry more text than the box takes, and the counter says so.
    this.#view.holdToCap();
  }

  /** Hold the box to the cap, which every keystroke has to do at once. */
  holdToCap(): void {
    this.#view.holdToCap();
  }

  /** What the address bar carries, and what Copy link copies. */
  state(): WritableState {
    const { settings, tiers } = this.#startup;

    return currentState(this.#elements, settings, tiers);
  }

  /** Segment what is in the box, then draw it. */
  segment(): void {
    const dictionary = this.#dictionary;
    if (dictionary === undefined) {
      return;
    }

    this.#view.holdToCap();

    const text = this.#elements.input.value;
    this.#words = text.trim() === "" ? [] : segmentedWords(dictionary, text);

    this.#draw();
    rememberUrl(this.state());
  }

  /**
   * Put the current list on the page, in the current order.
   *
   * Apart from {@link SegmenterSession.segment} because reordering and
   * changing the pinyin notation both land here and neither needs the text
   * read again. Ordering a few hundred gathered words is the cheap half.
   */
  #draw(): void {
    const { notation } = this.#startup.settings.options;

    this.#view.show(this.#words, this.#order, notation);
  }

  /** Show the same words again, in the order a reader asked for. */
  reorder(order: WordOrder): void {
    this.#order = order;
    this.#draw();
  }

  /**
   * What the copy button puts on the clipboard.
   *
   * The list in the order it is shown rather than the order it was gathered
   * in, so that a spreadsheet somebody pastes into arrives sorted the way the
   * page was.
   */
  copied(): CopyFlavours | undefined {
    const { notation } = this.#startup.settings.options;

    return wordsCopy(orderedWords(this.#words, this.#order), notation);
  }

  /** Take a dictionary and read the text with it. */
  async load(tier: ServedTier): Promise<void> {
    const dictionary = await this.#startup.tiers.load(tier);

    if (dictionary !== undefined) {
      this.#dictionary = dictionary;
      this.segment();
    }
  }

  /** Offer the other dictionary tiers, and load the one this visit wants. */
  async open(): Promise<void> {
    const { tiers, tier } = this.#startup;

    tiers.wireOffers(async (asked) => this.load(asked));

    await this.load(await tiers.opening(tier));
  }
}
